import { useEffect, useRef, useState } from "react";
import {
  ARCADA_DECIDUA_INFERIOR,
  ARCADA_DECIDUA_SUPERIOR,
  denticaoInicial,
  ehDeciduo,
  type Denticao,
} from "@bookdental/schemas";
import { cn } from "@/lib/cn";
import { Arcada } from "@/components/odontograma/Arcada";
import { SeletorDeDenticao } from "@/components/odontograma/SeletorDeDenticao";
import { FDI_LOWER_ARCH, FDI_UPPER_ARCH } from "./fdi-teeth";

function archButtonCls(active: boolean) {
  return cn(
    "inline-flex h-8 w-full items-center justify-center rounded-md border px-2.5 text-[11px] font-medium transition-colors",
    active
      ? "border-brand-300 bg-brand-50 text-brand-700 dark:border-primary/40 dark:bg-primary/10 dark:text-primary"
      : "border-zinc-200 bg-white text-zinc-600 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 dark:border-border dark:bg-card dark:text-muted-foreground dark:hover:border-primary/40 dark:hover:bg-primary/10 dark:hover:text-primary",
  );
}

type Props = {
  selectedFdi: string[];
  odontogramKey: number;
  /** A seleção inteira (as duas dentições), já com a mudança feita na arcada visível. */
  onChange: (selected: string[]) => void;
  onToggleArch: (arch: string[], isSelected: boolean) => void;
  /** Data de nascimento do paciente, quando a tela conhece — criança abre na decídua. */
  nascimento?: Date | string | null;
};

/** Painel do odontograma — arcadas + widget — compartilhado pelo pedido e pelo editor do laboratório. */
export function ProsthesisTeethPicker({ selectedFdi, odontogramKey, onChange, onToggleArch, nascimento }: Props) {
  const [denticao, setDenticao] = useState<Denticao>(() => denticaoInicial({ dentes: selectedFdi, nascimento }));
  // A data de nascimento pode chegar depois do primeiro desenho: decide de novo enquanto a pessoa
  // não tocou em nada.
  const tocou = useRef(false);
  useEffect(() => {
    if (!tocou.current && selectedFdi.length === 0) setDenticao(denticaoInicial({ nascimento }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nascimento]);

  const superior = denticao === "decidua" ? ARCADA_DECIDUA_SUPERIOR : FDI_UPPER_ARCH;
  const inferior = denticao === "decidua" ? ARCADA_DECIDUA_INFERIOR : FDI_LOWER_ARCH;
  const isUpperSelected = superior.every((f) => selectedFdi.includes(f));
  const isLowerSelected = inferior.every((f) => selectedFdi.includes(f));
  const deciduos = selectedFdi.filter(ehDeciduo).length;

  return (
    <div className="w-full shrink-0 self-start rounded-xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-border dark:bg-card lg:w-[300px]">
      <div className="flex flex-col items-center gap-2">
        <SeletorDeDenticao
          valor={denticao}
          onMudar={(d) => {
            tocou.current = true;
            setDenticao(d);
          }}
          contagem={{ permanente: selectedFdi.length - deciduos, decidua: deciduos }}
        />
        <button type="button" className={archButtonCls(isUpperSelected)} onClick={() => onToggleArch(superior, isUpperSelected)}>
          {denticao === "decidua" ? "Arcada decídua superior" : "Arcada superior"}
        </button>
        <div className="w-full max-w-[220px]">
          <Arcada
            denticao={denticao}
            selecionados={selectedFdi}
            chave={odontogramKey}
            onSelecionar={(lista) => {
              tocou.current = true;
              onChange(lista);
            }}
          />
        </div>
        <button type="button" className={archButtonCls(isLowerSelected)} onClick={() => onToggleArch(inferior, isLowerSelected)}>
          {denticao === "decidua" ? "Arcada decídua inferior" : "Arcada inferior"}
        </button>
      </div>
    </div>
  );
}
